import React from 'react';
import type { FlagKey } from '../registry';
import type { BucketBy } from './FeatureFlagContext';
import { useFeatureFlag } from './useFeatureFlag';
export interface FeatureFlagProps {
  /** The flag key to evaluate */
  flagKey: FlagKey;
  /** Bucketing mode: 'user' or 'device' */
  bucketBy: BucketBy;
  /** Content to render when the flag is enabled */
  children: React.ReactNode;
  /** Optional fallback to render when the flag is disabled */
  fallback?: React.ReactNode;
  /** Optional fallback to render while the provider is initializing (isReady === false).
   *  When omitted, falls back to SSR seed value or false (existing behavior). */
  loadingFallback?: React.ReactNode;
  /** Optional attributes for Flipt targeting rules (e.g., { email: 'user@zep.us' }) */
  context?: Record<string, string>;
}

/**
 * Declarative component for conditionally rendering content
 * based on a boolean feature flag.
 *
 * @example
 * ```tsx
 * <FeatureFlag flagKey="play.new_lobby_ui">
 *   <NewLobbyUI />
 * </FeatureFlag>
 *
 * <FeatureFlag flagKey="play.redesign" fallback={<OldDesign />}>
 *   <NewDesign />
 * </FeatureFlag>
 *
 * // With targeting context (e.g., email-based rules)
 * <FeatureFlag flagKey="test-ui" context={{ email: user.snsId }}>
 *   <TestUI />
 * </FeatureFlag>
 * ```
 */
export function FeatureFlag({
  flagKey,
  bucketBy,
  children,
  fallback = null,
  loadingFallback,
  context
}: FeatureFlagProps) {
  const {
    enabled,
    isReady
  } = useFeatureFlag(flagKey, {
    bucketBy,
    context
  });
  if (!isReady && loadingFallback !== undefined) {
    return <>{loadingFallback}</>;
  }
  return <>{enabled ? children : fallback}</>;
}